import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import moment from 'moment'

import { colors } from '../../../../constants'
import ContentButton from '../../../../containers/Content/Content/ContentButton'
import { useSize } from '../../../utils'
import { Centered, Column, Input, Loader, Margin, Padding, Table } from '../../..'
import {
    CloseButton,
    HeaderBar,
    PageTitle,
    ShadowColumn,
    Unselected,
} from './Content.style'
import PlaylistName from './PlaylistName'

const playlistsTableDisplay = ({ t, bulkSelectionPlaylists, selectedId }) => [
    {
        onlyOnMobile: true,
        field: 'name',
        label: t('Name'),
        props: null,
        transform: null,
        width: '300px',
    },
    {
        field: null,
        label: t('Name'),
        props: null,
        transform: (playlist) => {
            return <PlaylistName selectedId={selectedId}>{playlist}</PlaylistName>
        },
        width: '60%',
    },
    {
        field: 'date_end',
        label: 'ends',
        props: { align: 'left' },
        transform: (date) => {
            if (date) return moment(new Date(date * 1000)).fromNow()
        },
        width: '20%',
    },
    {
        field: null,
        label: null,
        props: { align: 'right' },
        transform: ({ selected, id }) => (
            <Input type="check" value={selected} onChange={(e) => bulkSelectionPlaylists(e, id)} borderColor={colors.bluedarkest} isInRow>
                {t('View')}
            </Input>
        ),
        width: '20px',
    },
]

const ContentPlaylist = (props) => {
    const {
        channelName,
        formPlaylists,
        isWorking,
        onClickPlaylist,
        onOpenFormPlaylists,
        onRemovePlaylists,
        onTextChangePlaylists,
        onChangePlaylistInput,
        optionsPlaylists,
        selectedPlaylist,
        playlists,
        expander,
        bulkSelectionPlaylists,
    } = props
    const { t } = useTranslation()
    const { isMobile } = useSize()
    const location = useLocation()
    const params = useParams()
    const playlistCounter = playlists instanceof Array
        ? playlists.filter((playlist) => playlist.selected).length
        : 0

    const storeContent = useSelector((state) => state.content);
    const playlistRef = useRef(storeContent.selected);

    useEffect(() => {
        if (storeContent.selected.channels) {
            if (storeContent.selected.channels.playlists) {
                playlistRef.current = storeContent.selected.channels.playlists;
            }
        }
    }, [storeContent.selected.channels]);

    const playlist = playlistRef.current;
    const isChannelSelected = !!params.channel
    const backUrl = location.pathname.split('/').slice(0,-1).join('/')

    return (
        <ShadowColumn isMobile={isMobile} isSelected={isChannelSelected}>
            {!playlists && !isWorking && <Unselected>{t('content-channel-unselected')}</Unselected>}
            {!playlists && isWorking && (
                <Unselected>
                    <Centered horizontal>
                        <Margin size="lg"><Loader /></Margin>
                    </Centered>
                </Unselected>
            )}
            {playlists && (
                <>
                    <Table
                        data={playlists}
                        display={playlistsTableDisplay({
                            bulkSelectionPlaylists,
                            selectedId: selectedPlaylist,
                            t,
                        })}
                        keyField="id"
                        isLoading={isWorking}
                        onClickRow={onClickPlaylist}
                        sortable={['name']}
                        expander={expander}
                        selectedItem={selectedPlaylist}
                        addWithSuggestions={{
                            onChange: onChangePlaylistInput,
                            onTextChange: onTextChangePlaylists,
                            options: optionsPlaylists,
                            placeholder: 'Add existing playlists...',
                        }}
                        complexTable
                        hideDeviceButtons
                    >
                        <>
                            <Padding size="md" color={colors.white} height="116px" style={{ fontSize: '30px' }}>
                                {isChannelSelected && isMobile && (
                                    <CloseButton to={backUrl} replace>&lt;&nbsp;Back to Channels</CloseButton>
                                )}
                                <PageTitle>
                                    {channelName}
                                    <span className="names-separator">&nbsp;&gt;&nbsp;</span>
                                    {t('Playlists')}
                                </PageTitle>
                            </Padding>
                            <HeaderBar
                                style={{
                                    display: 'flex',
                                    alignContent: 'center',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    }}
                                >
                                <Padding left right size="sm">
                                    {!isWorking && t('filter-counter', {
                                        filtered: playlist.length,
                                        total: playlist.length,
                                    })}
                                </Padding>
                            </HeaderBar>
                            <Padding size="xs">
                                <Padding top right size="xs">
                                    <Padding right size="xs">
                                        <Column width="50%">
                                            <Padding left size="sm">
                                                <Padding bottom size="xs" />
                                            </Padding>
                                        </Column>
                                        <Column right width="50%">
                                            <ContentButton onClick={onOpenFormPlaylists}>
                                                <FontAwesomeIcon icon="plus" size="1x" />
                                            </ContentButton>
                                            <ContentButton
                                                disabled={!playlistCounter}
                                                onClick={() => {
                                                    if (playlistCounter) onRemovePlaylists(playlists)
                                                }}
                                                theme="secondaryDanger"
                                            >
                                                <FontAwesomeIcon icon="trash-alt" size="1x" />
                                            </ContentButton>
                                        </Column>
                                </Padding>
                                </Padding>
                            </Padding>
                        </>
                    </Table>
                    {formPlaylists}
                </>
            )}
        </ShadowColumn>
    )
}

ContentPlaylist.propTypes = {
    channelName: PropTypes.string,
    formPlaylists: PropTypes.element,
    isWorking: PropTypes.bool,
    onClickPlaylist: PropTypes.func,
    onOpenFormPlaylists: PropTypes.func,
    onRemovePlaylists: PropTypes.func,
    onTextChangePlaylists: PropTypes.func,
    onChangePlaylistInput: PropTypes.func,
    optionsPlaylists: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            value: PropTypes.string,
        })
    ),
    playlists: PropTypes.arrayOf(PropTypes.shape({})),
    isMobile: PropTypes.bool,
    bulkSelectionPlaylists: PropTypes.func,
    selectedPlaylist: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    expander: PropTypes.func,
}

ContentPlaylist.defaultProps = {
    bulkSelectionPlaylists: () => null,
    channelName: '',
    expander: null,
    formPlaylists: null,
    isMobile: false,
    isWorking: false,
    onChangePlaylistInput: () => null,
    onClickPlaylist: () => null,
    onOpenFormPlaylists: () => null,
    onRemovePlaylists: () => null,
    onTextChangePlaylists: () => null,
    optionsPlaylists: [],
    selectedPlaylist: -1,
}

export default ContentPlaylist
